import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Divider } from "antd";
import { Button, Checkbox, Input, Modal, Radio, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { $t } from "~/i18n.js";
import { updateChecklist } from "~/redux/days/days.actions.js";
import { LastUpdated } from "../atom/LastUpdated.jsx";

/**
 * Render a single checklist item value read-only (for audit / dagVerslag views).
 */
export const ChecklistItemReadOnly = ({ item }) => {
  const { name, input_type, value, user_name, timestamp, required } = item;
  const renderValue = () => {
    if (value === null || value === undefined)
      return <p className="gray">{$t("Niet beantwoord")}</p>;
    switch (input_type) {
      case "checkbox":
        return value ? (
          <CheckCircleOutlined style={{ color: "green" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "red" }} />
        );
      case "multi":
        return Array.isArray(value) ? (
          value.map((v, i) => <Tag key={i}>{v}</Tag>)
        ) : (
          <Tag>{String(value)}</Tag>
        );
      default:
        return <span>{String(value)}</span>;
    }
  };

  return (
    <div style={{ padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
      <p style={{ fontSize: 11, color: "#999", margin: 0 }}>
        {name}
        {required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
      </p>
      <div style={{ marginTop: 2 }}>{renderValue()}</div>
    </div>
  );
};

/**
 * DayChecklistModal
 *
 * Interactive mode: fill in checklist answers and submit.
 * Read-only mode: show who answered what and when (audit trail).
 */
export const DayChecklistModal = ({
  visible,
  type, // 'start' | 'end'
  items = [],
  dagId,
  onClose,
  onComplete,
  readOnly = false,
}) => {
  const dispatch = useDispatch();
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  // Pre-populate answers from existing item values
  useEffect(() => {
    if (!visible) return;
    const initial = {};
    items.forEach((item) => {
      if (item.value !== null && item.value !== undefined) {
        initial[item.field] = item.value;
      }
    });
    setAnswers(initial);
  }, [visible, items]);

  const setAnswer = (field, value) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
  };

  // Submit is enabled only when all required items have an answer
  const allRequiredAnswered = items
    .filter((item) => item.required)
    .every((item) => {
      const val = answers[item.field];
      return val !== null && val !== undefined && val !== "";
    });

  const handleSubmit = async () => {
    setSaving(true);
    await dispatch(updateChecklist({ dag_id: dagId, type, answers }));
    setSaving(false);
    onComplete && onComplete();
  };

  const title =
    type === "start"
      ? $t("Checklist start van de dag")
      : $t("Checklist einde van de dag");

  const renderInteractiveItem = (item, idx) => {
    const { field, name, extra, input_type, options = [], required } = item;
    const value = answers[field];

    let inputNode;
    switch (input_type) {
      case "checkbox":
        inputNode = (
          <Checkbox
            checked={!!value}
            onChange={(e) => setAnswer(field, e.target.checked)}
          >
            {$t("Ja, bevestigd")}
          </Checkbox>
        );
        break;
      case "text":
        inputNode = (
          <Input
            value={value || ""}
            onChange={(e) => setAnswer(field, e.target.value)}
            size="small"
          />
        );
        break;
      case "number":
        inputNode = (
          <Input
            type="number"
            value={value || ""}
            onChange={(e) => setAnswer(field, e.target.value)}
            size="small"
            style={{ width: 120 }}
          />
        );
        break;
      case "date":
        inputNode = (
          <Input
            type="date"
            value={value || ""}
            onChange={(e) => setAnswer(field, e.target.value)}
            size="small"
            style={{ width: 160 }}
          />
        );
        break;
      case "time":
        inputNode = (
          <Input
            type="time"
            value={value || ""}
            onChange={(e) => setAnswer(field, e.target.value)}
            size="small"
            style={{ width: 120 }}
          />
        );
        break;
      case "multi":
        inputNode = (
          <Checkbox.Group
            options={(options || []).map((opt) => ({ label: opt, value: opt }))}
            value={value || []}
            onChange={(v) => setAnswer(field, v)}
          />
        );
        break;
      case "radio":
        inputNode = (
          <Radio.Group
            options={(options || []).map((opt) => ({ label: opt, value: opt }))}
            value={value}
            onChange={(e) => setAnswer(field, e.target.value)}
          />
        );
        break;
      default:
        inputNode = (
          <Input
            value={value || ""}
            onChange={(e) => setAnswer(field, e.target.value)}
            size="small"
          />
        );
    }

    return (
      <div
        key={idx}
        style={{
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <div style={{ marginBottom: 4 }}>
          <span className="bold">
            {name}
            {required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
          </span>
          {extra && (
            <p style={{ color: "#888", fontSize: 12, margin: "2px 0 6px" }}>
              {extra}
            </p>
          )}
        </div>
        {inputNode}
      </div>
    );
  };

  const hasBlockingUnanswered = items.some(
    (item) =>
      item.required &&
      (answers[item.field] === null ||
        answers[item.field] === undefined ||
        answers[item.field] === ""),
  );

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      width={500}
      footer={
        readOnly
          ? [
              <Button key="close" onClick={onClose}>
                {$t("Sluiten")}
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose}>
                {$t("Later")}
              </Button>,
              <Button
                key="submit"
                type="primary"
                loading={saving}
                disabled={!allRequiredAnswered}
                onClick={handleSubmit}
              >
                {$t("Opslaan")}
              </Button>,
            ]
      }
    >
      {readOnly ? (
        <div>
          {items.map((item, idx) => (
            <ChecklistItemReadOnly key={idx} item={item} />
          ))}

          {items && items.length > 0 && (
            <LastUpdated
              style={{ marginTop: 12 }}
              date_updated={items[0].timestamp}
              updated_by={items[0].user_name}
            />
          )}
        </div>
      ) : (
        <div>{items.map((item, idx) => renderInteractiveItem(item, idx))}</div>
      )}
    </Modal>
  );
};
